import { Field, useField, useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { tiersTableTitles, TIERS_ERRORS } from "utils/constants";
import { TierTableProps, TierType } from "./types";
import "./TiersTable.scss";
import { Button, Checkbox } from "@pickit/pickit-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NumericInput } from "component/inputs/NumericInput";
import { FieldArray } from "react-final-form-arrays";

const cols = [5, 5, 10];

const TiersTable = ({ name, disabled, testId }: TierTableProps) => {
  const { t } = useTranslation();
  const [selectedTiers, setSelectedTiers] = useState<number[]>([]);
  const field = useField(`tiers.${name}`);

  const touchedInputs = useRef<{
    [key in number]: { price: boolean; base: boolean };
  }>({ 0: { price: true, base: true } });
  const activeInput = useRef<{ index?: number; name?: "price" | "base" }>({});

  const hasError = useMemo(
    () => field.meta && field.meta.invalid && field.meta.touched,
    [field.meta]
  );
  const form = useForm();

  const toggleAll = useCallback(() => {
    const tiers: TierType[] = field.input.value;
    if (selectedTiers.length === tiers.length - 1) {
      setSelectedTiers([]);
    } else {
      setSelectedTiers([]);
      tiers.forEach((value: TierType, index: number) => {
        if (index > 0) setSelectedTiers((prev) => prev.concat(index));
      });
    }
  }, [field.input.value, selectedTiers.length]);

  const getErrorMessage = useCallback(() => {
    let errorCode: string | undefined;
    field.meta.error.forEach(
      (tier: { price: string; base: string }, index: number) => {
        if (tier) {
          const useGenericMessage =
            (errorCode &&
              ((tier.price && tier.price !== errorCode) ||
                (tier.base && tier.base !== errorCode))) ||
            (tier.price &&
              tier.base &&
              tier.price !== tier.base &&
              field.input.value[index] &&
              field.input.value[index].price !== null &&
              field.input.value[index].base !== null);

          errorCode = useGenericMessage
            ? TIERS_ERRORS.GENERIC_ERROR
            : field.input.value[index] &&
              field.input.value[index].base !== null &&
              tier.base
            ? tier.base
            : tier.price;
        }
      }
    );
    return t(`polygonPricing:errorMessage${errorCode}`);
  }, [field.input.value, field.meta.error, t]);

  const showError = useCallback(() => {
    let activateError = false;
    field.meta.error.forEach(
      (error: { price?: string; base?: string } | undefined, index: number) => {
        if (error) {
          if (error.price) {
            activateError =
              error.price !== TIERS_ERRORS.REQUIRED_ERROR ||
              field.input.value[index]?.price !== null ||
              activateError;
          }
          if (error.base) {
            activateError =
              error.base !== TIERS_ERRORS.REQUIRED_ERROR ||
              field.input.value[index]?.base !== null ||
              activateError;
          }
        }
      }
    );
    return activateError;
  }, [field.input.value, field.meta.error]);

  useEffect(() => {
    let activateError;
    if (field.meta.error) {
      activateError = showError();
    }
    if (hasError && !activateError) {
      form.resetFieldState(`tiers.${name}`);
    }
    if (
      activateError &&
      activeInput.current.index &&
      activeInput.current.name &&
      touchedInputs.current &&
      touchedInputs.current[activeInput.current.index] &&
      touchedInputs.current[activeInput.current.index][activeInput.current.name]
    ) {
      field.input.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.input.value, field.meta.error, name, showError]);

  const handleBlur = useCallback(
    (index?, value?) => {
      let activateError =
        field.meta?.error?.[index] && field.meta?.error?.[index][value];
      if (field.input.value[index][value] === null) {
        let newValue =
          value === "price"
            ? {
                base: field.input.value[index].base,
              }
            : {
                price: field.input.value[index].price,
              };
        field.input.value.splice(index, 1, newValue);
      }
      if (field.meta.error && !activateError) {
        activateError = showError();
      }
      if (activateError) field.input.onBlur();
    },
    [field.input, field.meta.error, showError]
  );

  return (
    <div className="tiers-container">
      <h3 className="tier-title" data-testId={`${testId}-title`}>
        {t(`polygonPricing:${name}Tiers`)}
      </h3>
      <div className="tiers-table">
        <div className="tiers-table-row tiers-table-header">
          <div className="tiers-table-col-sm-2" />
          {tiersTableTitles.map(
            (title: { label: string; id: string }, index: number) => (
              <div
                key={title.label}
                className={`tiers-table-col-sm-${cols[index]}`}
                data-testId={`${testId}-header-${title.id}`}
              >
                {t(title.label)}
              </div>
            )
          )}
          <div className="tiers-table-col-sm-2">
            <Checkbox
              value={
                selectedTiers.length === field.input.value.length - 1 &&
                field.input.value.length > 1
              }
              onChange={() => toggleAll()}
              testId={`${testId}-select-all`}
            />
          </div>
        </div>
        {!disabled ? (
          <FieldArray name={`tiers.${name}`}>
            {({ fields }) => (
              <div className="tiers-table-body">
                <div className="tiers-inputs-container">
                  {fields.map((name, index) => (
                    <div className="tiers-table-row">
                      <div className="tiers-table-col-2" />
                      <div
                        className={`tiers-table-col-${cols[0]} tier-number`}
                        data-testId={`${testId}-tier-${index}-number`}
                      >
                        {index}
                      </div>
                      <div className={`tiers-table-col-${cols[1]}`}>
                        <Field
                          name={`${name}.base`}
                          component={NumericInput}
                          className="Admin-Pickers-input table-input"
                          placeholder="-"
                          maxInt={4}
                          disabled={index === 0}
                          showErrorMessage={false}
                          allowDecimal={false}
                          onBlur={() => {
                            handleBlur(index, "base");
                            touchedInputs.current = {
                              ...touchedInputs.current,
                              [index]: {
                                price:
                                  touchedInputs.current[index]?.price ?? false,
                                base: true,
                              },
                            };
                            activeInput.current = {};
                          }}
                          onChange={() =>
                            (activeInput.current = {
                              index: index,
                              name: "base",
                            })
                          }
                          testId={`${testId}-tier-${index}-base`}
                        />
                      </div>
                      <div className={`tiers-table-col-${cols[2]}`}>
                        <Field
                          name={`${name}.price`}
                          component={NumericInput}
                          className="Admin-Pickers-input table-input"
                          placeholder="-"
                          disabled={index === 0}
                          allowDecimal
                          maxDecimal={2}
                          maxInt={5}
                          showErrorMessage={false}
                          onBlur={() => {
                            handleBlur(index, "price");
                            touchedInputs.current = {
                              ...touchedInputs.current,
                              [index]: {
                                base:
                                  touchedInputs.current?.[index].base || false,
                                price: true,
                              },
                            };

                            activeInput.current = {};
                          }}
                          onChange={() =>
                            (activeInput.current = {
                              index: index,
                              name: "price",
                            })
                          }
                          testId={`${testId}-tier-${index}-price`}
                        >
                          {(props) => (
                            <div className="pesos-input-container">
                              <p className={"pesos-placeholder"}>
                                {t("polygonPricing:divisa")}
                              </p>
                              <NumericInput {...props} />
                            </div>
                          )}
                        </Field>
                      </div>
                      <div className="tiers-table-col-sm-2">
                        <Checkbox
                          disabled={index === 0}
                          value={selectedTiers.includes(index)}
                          onChange={() =>
                            setSelectedTiers((prev) =>
                              prev.includes(index)
                                ? prev.filter((arrayValue: number) => {
                                    return arrayValue !== index;
                                  })
                                : prev.concat(index)
                            )
                          }
                          testId={`${testId}-tier-${index}-select`}
                        />
                      </div>
                      <div className="tiers-table-col-2" />
                    </div>
                  ))}
                </div>
                <div className="tiers-table-row">
                  <div className="tiers-table-col-2" />
                  <div className="tiers-table-col tiers-table-buttons">
                    <Button
                      hierarchy="minimal"
                      type="button"
                      label={t("polygonPricing:addRow")}
                      iconName="add"
                      onClick={() => {
                        if (!hasError) {
                          form.resetFieldState(`tiers.${name}`);
                        }
                        fields.push({ base: null, price: null });
                      }}
                      disabled={fields.length === 100}
                      testId={`${testId}-add`}
                    />
                    <Button
                      hierarchy="minimal"
                      type="button"
                      label={t("global:delete")}
                      iconName="trash"
                      iconPosition="right"
                      disabled={selectedTiers.length === 0}
                      onClick={() => {
                        selectedTiers.sort((a, b) => b - a);
                        selectedTiers.forEach((tier) => {
                          fields.remove(tier);
                        });
                        setSelectedTiers([]);
                      }}
                      testId={`${testId}-delete`}
                    />
                  </div>
                  <div className="tiers-table-col-2" />
                </div>
              </div>
            )}
          </FieldArray>
        ) : (
          <div className="workzones-empty-message empty-message">
            <p data-testId={`${testId}-empty-message`}>
              {t("workzonesTable:selectRegion")}
            </p>
          </div>
        )}
      </div>

      <label>{hasError && getErrorMessage()}</label>
    </div>
  );
};

export default TiersTable;
